// 메인 라우터 — React.lazy 코드 스플리팅 + AppLayout 통합
// 4단계 최종 통합: 모든 보호 라우트를 AppLayout으로 래핑
import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/authStore'
import { setSentryUser } from '@/lib/sentry'
import { ProtectedRoute, PublicOnlyRoute, AuthOnlyRoute } from '@/components/guards/RouteGuard'
import AppLayout from '@/components/AppLayout'
import AiChat from '@/components/AiChat'

// ─── 코드 스플리팅: 동적 임포트로 초기 번들 최소화 ───
const LandingPage       = lazy(() => import('@/pages/landing/LandingPage'))
const LoginPage         = lazy(() => import('@/pages/auth/LoginPage'))
const OnboardingPage    = lazy(() => import('@/pages/auth/OnboardingPage'))
const ProfileSelectPage = lazy(() => import('@/pages/auth/ProfileSelectPage'))
const DashboardPage     = lazy(() => import('@/pages/DashboardPage'))
const SalesPage       = lazy(() => import('@/pages/sales/SalesPage'))
const SchedulePage    = lazy(() => import('@/pages/schedule/SchedulePage'))
const ReviewPage      = lazy(() => import('@/pages/reviews/ReviewPage'))
const MarketingPage   = lazy(() => import('@/pages/marketing/MarketingPage'))
const MenuPage        = lazy(() => import('@/pages/menu/MenuPage'))
const OrderPage       = lazy(() => import('@/pages/orders/OrderPage'))
const IngredientsPage = lazy(() => import('@/pages/ingredients/IngredientsPage'))
const AccountingPage  = lazy(() => import('@/pages/accounting/AccountingPage'))
const SupplierPage    = lazy(() => import('@/pages/supplier/SupplierPage'))
const SettingsPage    = lazy(() => import('@/pages/settings/SettingsPage'))
const BillingPage     = lazy(() => import('@/pages/billing/BillingPage'))
const TaxInvoicePage  = lazy(() => import('@/pages/billing/TaxInvoicePage'))
const FaqPage         = lazy(() => import('@/pages/faq/FaqPage'))
const ChatPage        = lazy(() => import('@/pages/chat/ChatPage'))
const TrendsPage      = lazy(() => import('@/pages/trends/TrendsPage'))
// Launch A2/A3: 법무 페이지 (공개 — 인증 X)
const PrivacyPage     = lazy(() => import('@/pages/legal/PrivacyPage'))
const TermsPage       = lazy(() => import('@/pages/legal/TermsPage'))
// F2~F5: 에이전트 고급 도구 (Plan, Compare, Memory Search, Audit)
const AgentToolsPage  = lazy(() => import('@/pages/agent/AgentToolsPage'))
// G6: 운영자 admin (X-Admin-Key 인증, 공개 라우트지만 키 없으면 차단)
const AdminPage       = lazy(() => import('@/pages/admin/AdminPage'))
// P0-2: 매장 정지 안내 페이지
const SuspendedPage   = lazy(() => import('@/pages/SuspendedPage'))
// H2/H3/H5: 외부 연동 통합 페이지 (네이버/POS/Instagram/n8n)
const IntegrationsPage = lazy(() => import('@/pages/settings/IntegrationsPage'))
// J1/J2: 공개 변경이력 + 로드맵 (인증 X)
const ChangelogPage    = lazy(() => import('@/pages/public/ChangelogPage'))
const RoadmapPage      = lazy(() => import('@/pages/public/RoadmapPage'))
// J3~J6: 차별화 기능 (청결/퀴즈/음성/벤치마크)
const DifferentiationPage = lazy(() => import('@/pages/differentiation/DifferentiationPage'))

// ─── 세션 복원 초기화 (App 최상위에서 한 번만 실행) ───
// RouteGuard보다 먼저 getSession() 호출 → isAuthLoading=false 전환
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { setUser, setSession, setStoreInfo, setIsOnboarded, setIsAuthLoading } = useAuthStore()

  useEffect(() => {
    // 1) localStorage에서 Supabase 토큰 복원
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setSentryUser(session?.user?.id ?? null)

      // 세션 있으면 매장 정보도 DB에서 복원
      if (session?.user) {
        const { data } = await supabase
          .from('stores')
          .select('*')
          .eq('id', session.user.id)
          .single()
        if (data) {
          setStoreInfo(data)
          setIsOnboarded(true)
        }
      }

      // ✅ 세션 복원 완료 → 라우트 가드 리다이렉트 허용
      setIsAuthLoading(false)
    }).catch(() => {
      setIsAuthLoading(false)
    })

    // 2) 세션 변경 구독 (로그인/로그아웃/토큰 갱신 실시간 감지)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        setSentryUser(session?.user?.id ?? null)

        if (event === 'SIGNED_IN' && session?.user) {
          const { data } = await supabase
            .from('stores')
            .select('*')
            .eq('id', session.user.id)
            .single()
          if (data) {
            setStoreInfo(data)
            setIsOnboarded(true)
          }
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [setUser, setSession, setStoreInfo, setIsOnboarded, setIsAuthLoading])

  return <>{children}</>
}

// ─── 페이지 로딩 fallback ───
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4A574]" />
        <p className="text-sm text-gray-400">불러오는 중...</p>
      </div>
    </div>
  )
}

function App() {
  return (
    <BrowserRouter>
      {/* ✅ 세션 복원 초기화 — RouteGuard보다 먼저 getSession() 실행 */}
      <AuthInitializer>
      {/* 전역 토스트 알림 — WCAG AA 대비 4.5:1+ 보장 (Day 1-3) */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            fontFamily: 'Pretendard, sans-serif',
            fontSize: '14px',
            padding: '12px 16px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          },
          // success: bg green-100 + text green-900 + border green-700 → 10.8:1 ✅
          success: {
            iconTheme: { primary: '#15803d', secondary: '#dcfce7' },
            style: {
              background: '#dcfce7',
              color: '#14532d',
              border: '2px solid #15803d',
              fontWeight: 600,
            },
            ariaProps: { role: 'status', 'aria-live': 'polite' },
          },
          // error: bg red-100 + text red-900 + border red-700 → 11.2:1 ✅
          error: {
            iconTheme: { primary: '#b91c1c', secondary: '#fee2e2' },
            style: {
              background: '#fee2e2',
              color: '#7f1d1d',
              border: '2px solid #b91c1c',
              fontWeight: 600,
            },
            ariaProps: { role: 'alert', 'aria-live': 'assertive' },
          },
        }}
      />

      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* 공개 랜딩 페이지 */}
          <Route path="/" element={<LandingPage />} />

          {/* Launch A2/A3: 법무 페이지 — 누구나 (인증 X) */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          {/* G6: 운영자 — Admin 키 인증 */}
          <Route path="/admin" element={<AdminPage />} />
          {/* J1/J2: 공개 페이지 (인증 X) */}
          <Route path="/changelog" element={<ChangelogPage />} />
          <Route path="/roadmap" element={<RoadmapPage />} />

          {/* 공개 라우트 */}
          <Route element={<PublicOnlyRoute />}>
            <Route path="/login" element={<LoginPage />} />
          </Route>

          {/* 인증만 필요한 라우트 (온보딩/프로필 체크 X → 리다이렉트 루프 방지) */}
          <Route element={<AuthOnlyRoute />}>
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/profile-select" element={<ProfileSelectPage />} />
            {/* P0-2: 정지된 매장 안내 — ProtectedRoute는 여기로 리다이렉트 */}
            <Route path="/suspended" element={<SuspendedPage />} />
          </Route>

          {/* ✅ 보호된 라우트 — AppLayout으로 감싸기 */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/dashboard"   element={<DashboardPage />} />
              {/* 매출·운영 분석 */}
              <Route path="/sales/*"     element={<SalesPage />} />
              {/* 스마트 스케줄링 */}
              <Route path="/schedule/*"  element={<SchedulePage />} />
              {/* 리뷰 관리 */}
              <Route path="/reviews/*"   element={<ReviewPage />} />
              {/* 마케팅 콘텐츠 생성 */}
              <Route path="/marketing"   element={<MarketingPage />} />
              {/* 메뉴·레시피 & 교육 매뉴얼 */}
              <Route path="/menu/*"      element={<MenuPage />} />
              {/* 발주 관리 (Issue #8) */}
              <Route path="/orders"      element={<OrderPage />} />
              {/* 재료 원가 DB (Issue #18) */}
              <Route path="/ingredients" element={<IngredientsPage />} />
              {/* 회계·손익 (Issue #20) */}
              <Route path="/accounting"  element={<AccountingPage />} />
              {/* 공급처 절감 AI (Issue #9) */}
              <Route path="/supplier"    element={<SupplierPage />} />
              {/* 설정 (Wave 1 W1-A) */}
              <Route path="/settings"    element={<SettingsPage />} />
              {/* 결제·구독 (Day 3-1) */}
              <Route path="/billing"     element={<BillingPage />} />
              <Route path="/billing/success" element={<BillingPage />} />
              <Route path="/billing/fail"    element={<BillingPage />} />
              <Route path="/billing/tax-invoice" element={<TaxInvoicePage />} />
              {/* FAQ·고객지원 (Day 6-3) */}
              <Route path="/faq"         element={<FaqPage />} />
              {/* AI 어시스턴트 풀스크린 페이지 */}
              <Route path="/chat"        element={<ChatPage />} />
              {/* Phase 4: 시장 트렌드 분석 (7-source) */}
              <Route path="/trends"      element={<TrendsPage />} />
              {/* F2~F5: 에이전트 도구 모음 */}
              <Route path="/agent-tools" element={<AgentToolsPage />} />
              {/* H2/H3/H5: 외부 연동 (네이버, POS, Instagram, n8n) */}
              <Route path="/integrations" element={<IntegrationsPage />} />
              {/* J3~J6: 차별화 (청결/퀴즈/음성/벤치마크) */}
              <Route path="/lab" element={<DifferentiationPage />} />
            </Route>
          </Route>

          {/* 없는 경로 */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>

      {/* ✅ AI 어시스턴트 플로팅 채팅 (모든 페이지 공통) */}
      <AiChat />
      </AuthInitializer>
    </BrowserRouter>
  )
}

export default App
